/**
 * admin-members.ts - 회원(Member) 관련 Firestore 서비스
 *
 * 경로: users/{ownerUid}/centers/{centerId}/members/{memberId}
 *
 * 주의: 회원(Member)은 Firebase Authentication을 사용하지 않는다.
 * Firestore 문서 직접 읽기/쓰기만 사용한다.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  addDoc,
  deleteDoc,
  where,
  deleteField,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import type { Member } from "@vlab-corporation/vlab-pilates-service-interface/customer";
export type { Member };

// ---------------------------------------------------------------------------
// 내부 헬퍼
// ---------------------------------------------------------------------------

/**
 * 회원 컬렉션 참조를 반환한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 */
function membersCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "members",
  );
}

/**
 * 특정 회원 문서 참조를 반환한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param memberId - 회원 ID
 */
function memberDoc(ownerUid: string, centerId: number, memberId: string) {
  return doc(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "members",
    memberId,
  );
}

// ---------------------------------------------------------------------------
// 조회
// ---------------------------------------------------------------------------

/**
 * 특정 회원을 ID로 단건 조회한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param memberId - 회원 ID
 * @returns Member 또는 null (문서 없는 경우)
 */
export async function getAdminMember(
  ownerUid: string,
  centerId: number,
  memberId: string,
): Promise<Member | null> {
  const ref = memberDoc(ownerUid, centerId, memberId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return null;

  const data = snap.data() as Omit<Member, "id"> & { removedAt?: unknown };
  // 소프트 삭제된 회원(deleteMember가 removedAt 기록)은 없는 것으로 취급.
  if (data.removedAt != null) return null;

  return {
    id: snap.id,
    ...(data as Omit<Member, "id">),
  };
}

/**
 * 전체 회원 목록을 배열로 반환한다.
 *
 * `getAdminMembers()`는 이름 키 맵이라 동명이인 시 데이터 손실 가능.
 * 목록 페이지 등에서는 이 함수를 사용한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @returns Member 배열
 */
export async function getAdminMembersList(ownerUid: string, centerId: number): Promise<Member[]> {
  const col = membersCollection(ownerUid, centerId);
  const snap = await getDocs(col);
  return (
    snap.docs
      // 소프트 삭제된 회원(removedAt)은 목록에서 제외.
      .filter((d) => (d.data() as { removedAt?: unknown }).removedAt == null)
      .map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Member, "id">),
      }))
  );
}

/**
 * 전체 회원 목록을 이름 → Member 맵으로 반환한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @returns 이름(name) → Member 맵
 */
export async function getAdminMembers(
  ownerUid: string,
  centerId: number,
): Promise<Map<string, Member>> {
  const col = membersCollection(ownerUid, centerId);
  const snap = await getDocs(col);

  const map = new Map<string, Member>();
  for (const d of snap.docs) {
    const member: Member = {
      id: d.id,
      ...(d.data() as Omit<Member, "id">),
    };
    map.set(member.name, member);
  }

  return map;
}

/**
 * 이름으로 회원을 단건 조회한다.
 *
 * 동명이인이 있는 경우 첫 번째 결과를 반환한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param name - 조회할 회원 이름
 * @returns Member 또는 null (해당 이름의 회원 없는 경우)
 */
export async function getMemberByName(
  ownerUid: string,
  centerId: number,
  name: string,
): Promise<Member | null> {
  const col = membersCollection(ownerUid, centerId);
  const q = query(col, where("name", "==", name));
  const snap = await getDocs(q);

  if (snap.docs.length === 0) return null;

  const d = snap.docs[0];
  return {
    id: d.id,
    ...(d.data() as Omit<Member, "id">),
  };
}

// ---------------------------------------------------------------------------
// 수정
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 회원×강사 N:N 재등록 관계
// ---------------------------------------------------------------------------

/** 재등록 관계 조회 결과 */
export interface ReRegistrationEntry {
  docId: string;
  instructorId: string;
  reRegistrationDate: string;
}

/**
 * reRegistrations 컬렉션 참조를 반환한다.
 */
function reRegistrationsCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "reRegistrations",
  );
}

/**
 * 특정 회원의 재등록 관계 목록을 조회한다.
 */
export async function getMemberReRegistrations(
  ownerUid: string,
  centerId: number,
  memberId: string,
): Promise<ReRegistrationEntry[]> {
  const col = reRegistrationsCollection(ownerUid, centerId);
  const q = query(col, where("memberId", "==", memberId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as { instructorId: string; reRegistrationDate: string };
    return {
      docId: d.id,
      instructorId: data.instructorId,
      reRegistrationDate: data.reRegistrationDate,
    };
  });
}

/**
 * 회원×강사 재등록 관계를 설정한다.
 * 동일한 (memberId, instructorId) 쌍이 이미 있으면 업데이트한다.
 */
export async function setMemberInstructorReRegistration(
  ownerUid: string,
  centerId: number,
  memberId: string,
  instructorId: string,
  reRegistrationDate: string,
): Promise<void> {
  const col = reRegistrationsCollection(ownerUid, centerId);
  const q = query(
    col,
    where("memberId", "==", memberId),
    where("instructorId", "==", instructorId),
  );
  const snap = await getDocs(q);

  if (snap.docs.length > 0) {
    await updateDoc(snap.docs[0].ref, { reRegistrationDate });
  } else {
    await addDoc(col, { memberId, instructorId, reRegistrationDate });
  }
}

/**
 * 회원×강사 재등록 관계를 삭제한다.
 */
export async function clearMemberInstructorReRegistration(
  ownerUid: string,
  centerId: number,
  docId: string,
): Promise<void> {
  const ref = doc(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "reRegistrations",
    docId,
  );
  await deleteDoc(ref);
}

/**
 * 회원의 정산 기본 위탁대금(settlementUnitPrice)을 업데이트한다.
 *
 * null을 전달하면 해당 필드를 Firestore에서 삭제한다 (payScale 기본값으로 복귀).
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param memberId - 회원 ID
 * @param settlementUnitPrice - 정산 기본 위탁대금 (원) 또는 null (삭제)
 */
export async function updateMemberSettlementUnitPrice(
  ownerUid: string,
  centerId: number,
  memberId: string,
  settlementUnitPrice: number | null,
): Promise<void> {
  const ref = memberDoc(ownerUid, centerId, memberId);
  await updateDoc(ref, {
    settlementUnitPrice: settlementUnitPrice !== null ? settlementUnitPrice : deleteField(),
  });
}

// ---------------------------------------------------------------------------
// 동기화 메타데이터
// ---------------------------------------------------------------------------

/**
 * 센터의 회원 동기화 시각을 조회한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @returns membersSyncedAt ISO 문자열 또는 null
 */
export async function getMembersSyncedAt(
  ownerUid: string,
  centerId: number,
): Promise<string | null> {
  const ref = doc(getFirebaseFirestore(), "users", ownerUid, "centers", String(centerId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() as { membersSyncedAt?: string };
  return data.membersSyncedAt ?? null;
}
